import React, { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Download, Share, X, Smartphone } from "lucide-react";
import { toast } from "sonner";
import { isIos, isStandalone } from "../lib/push";

const DISMISS_KEY = "kw_install_dismissed_at";
const DISMISS_HOURS = 168; // a week

function recentlyDismissed() {
    try {
        const at = parseInt(localStorage.getItem(DISMISS_KEY) || "0", 10);
        if (!at) return false;
        return Date.now() - at < DISMISS_HOURS * 3600 * 1000;
    } catch {
        return false;
    }
}

export default function InstallAppButton() {
    const [deferred, setDeferred] = useState(null);
    const [installed, setInstalled] = useState(false);
    const [iosOpen, setIosOpen] = useState(false);
    const [available, setAvailable] = useState(false);

    useEffect(() => {
        if (isStandalone()) {
            setInstalled(true);
            return;
        }
        const onBefore = (e) => {
            e.preventDefault();
            setDeferred(e);
            setAvailable(true);
        };
        const onInstalled = () => {
            setInstalled(true);
            setAvailable(false);
            setDeferred(null);
            toast.success("KYLIE WEST added to home screen");
            try {
                sessionStorage.setItem("kw_just_installed", "1");
                // Reset push-banner dismissal so the upgraded prompt definitely appears
                localStorage.removeItem("kw_push_dismissed_at");
            } catch {
                /* ignore */
            }
            window.dispatchEvent(new CustomEvent("kw-just-installed"));
        };
        window.addEventListener("beforeinstallprompt", onBefore);
        window.addEventListener("appinstalled", onInstalled);
        // iOS doesn't fire beforeinstallprompt — always show the button when on iPhone
        if (isIos() && !isStandalone()) {
            setAvailable(true);
        }
        return () => {
            window.removeEventListener("beforeinstallprompt", onBefore);
            window.removeEventListener("appinstalled", onInstalled);
        };
    }, []);

    if (installed || !available) return null;
    if (recentlyDismissed()) {
        // The user actively closed the iOS dialog or said "Not now" recently —
        // we still want the discreet header button to remain available, just don't
        // auto-pop the modal. So keep returning the button.
    }

    const trigger = async () => {
        if (isIos()) {
            setIosOpen(true);
            return;
        }
        if (!deferred) {
            toast.info(
                "Use your browser menu → 'Install app' or 'Add to Home Screen'"
            );
            return;
        }
        try {
            deferred.prompt();
            const result = await deferred.userChoice;
            if (result.outcome === "accepted") {
                toast.success("KYLIE WEST added to home screen");
            }
            setDeferred(null);
            setAvailable(false);
        } catch {
            /* ignore */
        }
    };

    const dismissIos = () => {
        try {
            localStorage.setItem(DISMISS_KEY, String(Date.now()));
        } catch {
            /* ignore */
        }
        setIosOpen(false);
    };

    return (
        <>
            <button
                type="button"
                data-testid="install-app-btn"
                onClick={trigger}
                aria-label="Install app"
                className="flex items-center gap-1 px-2.5 py-1.5 rounded-full border border-[#FF5A00]/60 text-[10px] uppercase tracking-[0.15em] kw-orange hover:bg-[#FF5A00] hover:text-black transition-colors"
            >
                <Download size={12} />
                <span>Install</span>
            </button>

            <AnimatePresence>
                {iosOpen && (
                    <motion.div
                        data-testid="install-ios-modal"
                        className="fixed inset-0 z-[140] flex items-end sm:items-center justify-center bg-black/80 backdrop-blur-sm p-4"
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        exit={{ opacity: 0 }}
                        onClick={dismissIos}
                    >
                        <motion.div
                            className="w-full max-w-sm bg-[#121212] border border-white/10 rounded-3xl p-6 relative"
                            initial={{ y: 40, opacity: 0 }}
                            animate={{ y: 0, opacity: 1 }}
                            exit={{ y: 40, opacity: 0 }}
                            onClick={(e) => e.stopPropagation()}
                        >
                            <button
                                onClick={dismissIos}
                                className="absolute top-3 right-3 p-1.5 rounded-full text-white/60 hover:text-white"
                                aria-label="Close"
                            >
                                <X size={16} />
                            </button>
                            <div className="flex items-center gap-3 mb-4">
                                <Smartphone size={24} className="kw-orange" />
                                <div className="font-display text-xl uppercase tracking-tight">
                                    Add to Home Screen
                                </div>
                            </div>
                            <ol className="space-y-3 text-sm text-white/80 leading-relaxed">
                                <li className="flex items-start gap-3">
                                    <span className="kw-orange font-bold">
                                        1.
                                    </span>
                                    <span>
                                        Tap the <Share size={14} className="inline kw-orange" />{" "}
                                        <span className="kw-orange font-bold">
                                            Share
                                        </span>{" "}
                                        icon at the bottom of Safari.
                                    </span>
                                </li>
                                <li className="flex items-start gap-3">
                                    <span className="kw-orange font-bold">
                                        2.
                                    </span>
                                    <span>
                                        Scroll down and tap{" "}
                                        <span className="kw-orange font-bold">
                                            "Add to Home Screen"
                                        </span>
                                        .
                                    </span>
                                </li>
                                <li className="flex items-start gap-3">
                                    <span className="kw-orange font-bold">
                                        3.
                                    </span>
                                    <span>
                                        Tap{" "}
                                        <span className="kw-orange font-bold">
                                            Add
                                        </span>{" "}
                                        — KYLIE WEST will appear on your home
                                        screen like a real app.
                                    </span>
                                </li>
                            </ol>
                            <button
                                onClick={dismissIos}
                                className="w-full mt-6 py-3 kw-orange-bg text-black font-bold rounded-xl uppercase tracking-wider text-sm"
                            >
                                Got it
                            </button>
                        </motion.div>
                    </motion.div>
                )}
            </AnimatePresence>
        </>
    );
}
